import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, GraduationCap, MessageSquareText, Sparkles, CalendarX2, PartyPopper } from 'lucide-react';
import api from '../../lib/api';
import { fill } from '../../lib/nextSteps';
import StepDots from './StepDots';

const ICON = { mock_full: FileText, mock_any: FileText, course_section: GraduationCap, feedback_requested: MessageSquareText, visit: Sparkles };

function useStepAction(step, onChanged) {
  const navigate = useNavigate();
  return async () => {
    if (step.done_when === 'visit') { try { await api.post('/api/next-steps/done', { key: step.key }); onChanged?.(); } catch (e) { /* non-blocking */ } }
    const u = String(step.button_url || '').trim();
    if (/^https?:\/\//i.test(u)) window.open(u, '_blank', 'noopener,noreferrer'); else navigate(u || '/dashboard');
  };
}

/** Hero for a non-class step ("Take your mock test before the class" → Start mock test). Compact on phones. */
export default function StepHeroCard({ step, steps, vars, classNote, onChanged }) {
  const Icon = ICON[step.done_when] || Sparkles;
  const act = useStepAction(step, onChanged);
  return (
    <section className="relative overflow-hidden rounded-3xl md:rounded-[28px] bg-white border border-slate-200/80 shadow-sm p-4 sm:p-6 md:p-8" data-testid="ns-hero" data-kind="step" data-step={step.key}>
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#002FA7]" />
      <div className="space-y-4 md:space-y-5 pl-1">
        <StepDots steps={steps} activeKey={step.key} />
        {classNote && (
          <div className="inline-flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs font-medium px-3 py-2" data-testid="ns-class-note">
            <CalendarX2 size={14} className="mt-0.5 shrink-0 text-amber-600" /> <span>{classNote}</span>
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">
          <div className="flex items-start gap-3 md:contents">
            <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-[#002FA7] text-white flex items-center justify-center shrink-0 shadow-lg shadow-blue-900/20"><Icon size={22} /></div>
            <div className="flex-1 min-w-0">
              <h2 className="font-[Manrope] text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight" data-testid="ns-step-title">{fill(step.title, vars)}</h2>
              {step.description && <p className="text-sm md:text-base text-slate-600 mt-1 md:mt-2 max-w-2xl leading-relaxed" data-testid="ns-step-desc">{fill(step.description, vars)}</p>}
            </div>
          </div>
          <button type="button" onClick={act}
            className="inline-flex w-full md:w-auto items-center justify-center gap-2 rounded-full bg-[#002FA7] hover:bg-[#00248a] text-white font-bold px-6 py-3 md:px-7 md:py-3.5 text-sm md:text-base shadow-lg shadow-blue-900/20 md:hover:-translate-y-0.5 transition-colors md:transition-all shrink-0"
            data-testid="ns-step-cta">
            {fill(step.button_label, vars)} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

export function AllDoneCard({ texts, vars, steps }) {
  const navigate = useNavigate();
  const t = texts.all_done;
  return (
    <section className="rounded-3xl md:rounded-[28px] bg-gradient-to-br from-emerald-50 via-white to-white border border-emerald-200 p-4 sm:p-6 md:p-8 shadow-sm" data-testid="ns-hero" data-kind="done">
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">
        <div className="flex items-start gap-3 md:contents">
          <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-emerald-600/25"><PartyPopper size={22} /></div>
          <div className="flex-1 min-w-0">
            <h2 className="font-[Manrope] text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">{fill(t.title, vars)}</h2>
            <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">{fill(t.text, vars)}</p>
            <div className="mt-3"><StepDots steps={steps} activeKey="" /></div>
          </div>
        </div>
        <button type="button" onClick={() => navigate(t.button_url || '/study-plan')} className="inline-flex w-full md:w-auto items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 text-sm shadow-lg shadow-emerald-600/25 md:hover:-translate-y-0.5 transition-colors md:transition-all shrink-0" data-testid="ns-step-cta">
          {t.button_label} <ArrowRight size={15} />
        </button>
      </div>
    </section>
  );
}
